import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Statistic, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  UserResourceDashboardContentCard,
  UserResourceDashboardLayout,
} from '@/components/user-resource-dashboard/dashboard-layout'
import { DashboardPieChart } from '@/components/user-resource-dashboard/user-resource-dashboard-chart/dashboard-chart-pie'
import { Team } from '@/schemas'

import { TeamDetailDashboard } from './team-detail-dashboard'
import { TeamResourceWarningTable } from './team-resource-warning-table.component'
import { TeamTrendDashboardCard } from './team-trend-dashboard'

export const TeamUserResourceDashboard: React.FC<{ departmentId: any; team: Team }> = ({ departmentId, team }) => {
  const { t } = useTranslation()
  const ref = React.useRef<{ download: () => void }>(null)
  const resourceWarningRef = React.useRef<{ download: () => void }>(null)

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data: overviewData, run } = useRequest(
    {
      url: '/api/work-resource/statistics/team/general/infos',
      params: {
        startDate,
        endDate,
        departmentId,
      },
    },
    {
      manual: true,
      initialData: {
        tops: [],
        counts: {
          customer: 0,
          production: 0,
          devlop: 0,
          normal: 0,
        },
        total: {
          percentage: null,
          topTotalWorkload: 0,
          totalWorkload: 0,
        },
      },
    }
  )

  React.useEffect(() => {
    if (departmentId && startDate && endDate) {
      run()
    }
  }, [departmentId, startDate, endDate])

  return (
    <UserResourceDashboardLayout>
      <UserResourceDashboardContentCard
        title={t('资源总览')}
        titleInfo={'占用团队资源前六的项目总工时/团队资源总工时， “项目”指的是三大类项目：客户项目、产品项目和研发项目'}
      >
        <div className='flex'>
          <div className='flex-shrink-0'>
            <div className='font-medium text-center text-lg mb-4'>{t('团队资源占用前六的项目')}</div>
            <DashboardPieChart className='h-60 w-60' data={overviewData!.tops} total={overviewData!.total} />
          </div>
          <div className='flex mt-14 ml-20'>
            <div className='space-y-6'>
              <Statistic
                className='rounded-lg bg-blue-200 mr-10 p-4  w-75'
                title={t('客户项目数')}
                value={overviewData!.counts.customer}
              />
              <Statistic
                className='rounded-lg bg-green-100 p-4 w-75'
                title={t('产品项目数')}
                value={overviewData!.counts.production}
              />
            </div>
            <div className='space-y-6'>
              <Statistic
                className='rounded-lg bg-yellow-100 p-4 w-75'
                title={t('研发项目数')}
                value={overviewData!.counts.devlop}
              />
              <Statistic
                className='rounded-lg bg-gray-100 p-4 w-75'
                title={
                  <span>
                    {t('关联普通项目数')}
                    <Tooltip title={'关联在该团队下的普通/自定义项目数量'}>
                      <InfoCircleOutlined className='cursor-pointer text-sm ml-1' size={14} />
                    </Tooltip>
                  </span>
                }
                value={overviewData!.counts.normal}
              />
            </div>
          </div>
        </div>
      </UserResourceDashboardContentCard>
      <UserResourceDashboardContentCard title={t('资源趋势')}>
        <TeamTrendDashboardCard type='project' departmentId={departmentId} team={team} />
        <TeamTrendDashboardCard type='user' departmentId={departmentId} team={team} />
      </UserResourceDashboardContentCard>
      <UserResourceDashboardContentCard
        title={t('资源明细')}
        titleInfo={'产品项目中包含研发驱动与客户驱动项目'}
        operations={
          <Tooltip title={t('下载')}>
            <Button onClick={() => ref.current?.download()} shape='circle' icon={<DownloadOutlined />}></Button>
          </Tooltip>
        }
      >
        <TeamDetailDashboard ref={ref} departmentId={departmentId} />
      </UserResourceDashboardContentCard>
      <UserResourceDashboardContentCard
        title={'资源填写小于5人日列表'}
        operations={
          <Tooltip title={t('下载')}>
            <Button
              onClick={() => resourceWarningRef.current?.download()}
              shape='circle'
              icon={<DownloadOutlined />}
            ></Button>
          </Tooltip>
        }
      >
        <TeamResourceWarningTable ref={resourceWarningRef} departmentId={departmentId} team={team} />
      </UserResourceDashboardContentCard>
    </UserResourceDashboardLayout>
  )
}
